/**
 * main.js — Bar Experts
 * Entry point da aplicação Vue 3.
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createRouter, createWebHistory } from 'vue-router'
import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap/dist/js/bootstrap.bundle.min.js'
import './assets/main.css'
import './assets/interactions.css'
import { initGlobalRubberband } from './directives/rubberband'
import { stackTable } from './directives/stackTable'

// Imports globais de CSS do Canvas 7
import './assets/canvas/style.css'
import './assets/canvas/css/font-icons.css'
import './assets/canvas/css/swiper.css'
import './assets/canvas/css/custom.css'

// Imports globais de JS do Canvas 7
// import './assets/canvas/js/plugins.min.js'
// import './assets/canvas/js/functions.bundle.js'

import App from './App.vue'
import { useAuthStore } from './stores/auth'
import { trackPageView } from './services/telemetry'
import { applyPageMeta } from './seo/applyMeta'

// Lazy-loaded views
const Home = () => import('./views/Home.vue')
const Article = () => import('./views/Article.vue')
const Dashboard = () => import('./views/Dashboard.vue')
const Profile = () => import('./views/Profile.vue')
const Library = () => import('./views/Library.vue')
const Calculators = () => import('./views/Calculators.vue')
const ConsultorPanel = () => import('./views/ConsultorPanel.vue')
const Login = () => import('./views/Login.vue')
const NotFound = () => import('./views/NotFound.vue')
const Sandbox = () => import('./views/Sandbox.vue')
const AdminCourses = () => import('./views/AdminCourses.vue')
const AdminArticles = () => import('./views/AdminArticles.vue')
const AdminEditor = () => import('./views/AdminEditor.vue')
const AdminLibrary = () => import('./views/AdminLibrary.vue')
const Cursos = () => import('./views/Cursos.vue')
const CursoDetail = () => import('./views/CursoDetail.vue')
const Masterclasses = () => import('./views/Masterclasses.vue')
const MasterclassDetail = () => import('./views/MasterclassDetail.vue')
const ApostilasView = () => import('./views/ApostilasView.vue')
const CocktailsHome = () => import('./views/cocktails/CocktailsHome.vue')
const CocktailCollectionView = () => import('./views/cocktails/CocktailCollectionView.vue')
const CocktailDetailView = () => import('./views/cocktails/CocktailDetailView.vue')
const BookDetailView = () => import('./views/library/BookDetailView.vue')

const MenuPublicView = () => import('./views/MenuPublicView.vue')
const SuperAdminPanel = () => import('./views/SuperAdminPanel.vue')
const NewsletterManagerView = () => import('./views/NewsletterManagerView.vue')
const PrivacyPolicy = () => import('./views/PrivacyPolicy.vue')
const TermsOfService = () => import('./views/TermsOfService.vue')
const MailboxView = () => import('./views/MailboxView.vue')
const MediaGalleryView = () => import('./views/MediaGalleryView.vue')
const AdminChampionshipView = () => import('./views/AdminChampionshipView.vue')
const MagazineComingSoon = () => import('./views/MagazineComingSoon.vue')
const ChampionshipRegulation = () => import('./views/ChampionshipRegulation.vue')
const ChampionshipInternalRegulation = () => import('./views/ChampionshipInternalRegulation.vue')
const ChampionshipDossierView = () => import('./views/ChampionshipDossierView.vue')
const WolfAmongUsView = () => import('./views/WolfAmongUsView.vue')

const routes = [
  { 
    path: '/wolf-among-us', 
    component: WolfAmongUsView, 
    meta: { 
      title: 'Wolf Among Us — Apresentação de Marca & Elementos Ilustrados',
      hideHeader: true,
      hideFooter: true,
      hideAppMenu: true
    } 
  },
  { path: '/brand/wolf-among-us', redirect: '/wolf-among-us' },
  { path: '/apresentacao/wolf-among-us', redirect: '/wolf-among-us' },
  { path: '/', component: MagazineComingSoon, meta: { title: '2º Campeonato Off Z Wagon — O Desafio Proibicionista | Bar Experts', index: 0 } },
  { path: '/magazine', component: MagazineComingSoon, meta: { title: 'Magazine — Em Breve | Bar Experts' } },
  { 
    path: '/campeonato', 
    component: ChampionshipDossierView, 
    meta: { title: 'Dossiê Clandestino — 2º Campeonato Off Z Wagon', index: 0 }
  },
  { 
    path: '/regulamento', 
    component: ChampionshipRegulation, 
    meta: { title: 'Regulamento Oficial — 2º Campeonato Off Z Wagon' }
  },
  { path: '/campeonato/regulamento', redirect: '/regulamento' },
  { 
    path: '/regulamento-interno', 
    component: ChampionshipInternalRegulation, 
    meta: { title: 'Guia Operacional & Mentoria — Off Z Wagon' }
  },
  { path: '/campeonato/colaboradores', redirect: '/regulamento-interno' },
  { path: '/campeonato/staff', redirect: '/regulamento-interno' },
  { path: '/editorial', component: Home, meta: { title: 'Editorial — Bar Experts' } },
  // Página de consultoria desativada: links antigos voltam para a home
  { path: '/consultoria', redirect: '/' },
  { path: '/cursos', component: Cursos, meta: { title: 'Cursos — Bar Experts', index: 2 } },
  { path: '/cursos/:slug', component: CursoDetail, meta: { title: 'Curso — Bar Experts' } },
  { path: '/masterclass', component: Masterclasses, meta: { title: 'Masterclass — Bar Experts', index: 3 } },
  { path: '/masterclass/:slug', component: MasterclassDetail, meta: { title: 'Masterclass — Bar Experts' } },
  { path: '/cardapio/:slug', component: MenuPublicView, meta: { title: 'Cardápio — Bar Experts' } },
  { path: '/sandbox', component: Sandbox, meta: { title: 'Sandbox — Bar Experts' } },
  { path: '/login', component: Login, meta: { title: 'Entrar — Bar Experts' } },
  { path: '/artigos/:slug', component: Article, meta: { title: 'Artigo — Bar Experts' } },
  { path: '/coqueteis', component: CocktailsHome, meta: { title: 'Coquetéis — Acervo & Clássicos | Bar Experts', index: 1 } },
  { path: '/coqueteis/colecao/:slug', component: CocktailCollectionView, meta: { title: 'Coleção de Coquetéis — Bar Experts' } },
  { path: '/coqueteis/:slug', component: CocktailDetailView, meta: { title: 'Ficha Técnica de Coquetel — Bar Experts' } },
  { path: '/cocktails/:slug', redirect: to => `/coqueteis/${to.params.slug}` },
  { path: '/cocktails', redirect: '/coqueteis' },
  { path: '/biblioteca', component: Library, meta: { title: 'Biblioteca — Bar Experts' } },
  { path: '/biblioteca/livro/:slug', component: BookDetailView, meta: { title: 'Livro — Bar Experts' } },
  { path: '/biblioteca/apostilas', component: ApostilasView, meta: { title: 'Apostilas & Materiais de Estudo — Bar Experts' } },
  { path: '/apostilas', redirect: '/biblioteca/apostilas' },
  { path: '/materiais', redirect: '/biblioteca/apostilas' },
  // Calculadoras: ferramenta interna para bartenders logados (aparece só no submenu de quem tem nível)
  { path: '/calculadoras', component: Calculators, meta: { title: 'Calculadoras — Bar Experts', requiresLevel: 1 } },
  { path: '/calculators', redirect: '/calculadoras' },
  { path: '/privacidade', component: PrivacyPolicy, meta: { title: 'Política de Privacidade — Bar Experts' } },
  { path: '/termos', component: TermsOfService, meta: { title: 'Termos de Uso — Bar Experts' } },
  {
    path: '/dashboard',
    component: Dashboard,
    meta: { title: 'Dashboard — Bar Experts', requiresAuth: true },
  },
  {
    path: '/perfil',
    component: Profile,
    meta: { title: 'Meu Perfil — Bar Experts', requiresAuth: true },
  },
  
  {
    path: '/painel',
    component: ConsultorPanel,
    meta: { title: 'Painel Consultor — Bar Experts', requiresLevel: 3 },
  },
  // ── Galeria de Mídia Local (N1+) ──────────────────────────────────────
  {
    path: '/admin/midia',
    component: MediaGalleryView,
    meta: { title: 'Galeria de Mídia — Bar Experts', requiresAuth: true },
  },
  {
    path: '/midia',
    component: MediaGalleryView,
    meta: { title: 'Galeria de Mídia — Bar Experts', requiresAuth: true },
  },
  // ── Admin: Cursos, Turmas e Alunos (N4+) ──────────────────────────────
  {
    path: '/admin/cursos',
    component: AdminCourses,
    meta: { title: 'Gestão de Cursos — Bar Experts', requiresLevel: 4 },
  },
  // ── Admin: Motor Editorial Headless (N4+) ─────────────────────────────
  {
    path: '/admin/biblioteca',
    component: AdminLibrary,
    meta: { title: 'Curadoria Acervo — Bar Experts', requiresLevel: 4 },
  },
  {
    path: '/admin/artigos',
    component: AdminArticles,
    meta: { title: 'Admin — Artigos — Bar Experts', requiresLevel: 4 },
  },
  {
    path: '/admin/artigos/novo',
    component: AdminEditor,
    meta: { title: 'Novo Artigo — Bar Experts', requiresLevel: 4 },
  },
  {
    path: '/admin/artigos/:id/editar',
    component: AdminEditor,
    meta: { title: 'Editar Artigo — Bar Experts', requiresLevel: 4 },
  },
  // ── Admin: Newsletter & WhatsApp Hub (N4+) ──────────────────────────────
  {
    path: '/admin/newsletter',
    component: NewsletterManagerView,
    meta: { title: 'Newsletter & WhatsApp Hub — Bar Experts', requiresLevel: 4 },
  },
  // ── Mailbox Interno (Pinho & Carvalho / Admin) ─────────────────────────
  {
    path: '/mailbox',
    component: MailboxView,
    meta: { title: 'Mailbox — Bar Experts', requiresAuth: true },
  },
  // ── Admin: Mesa de Jurados & Campeonato (N4+) ─────────────────────────
  {
    path: '/admin/campeonato',
    component: AdminChampionshipView,
    meta: { title: 'Mesa de Jurados & Inscrições — 2º Campeonato Off Z Wagon', requiresLevel: 4 },
  },
  // ── Super Admin (N5) ─────────────────────────────
  {
    path: '/superadmin',
    component: SuperAdminPanel,
    meta: { title: 'Modo Deus — Bar Experts', requiresLevel: 5 },
  },
  { path: '/:pathMatch(.*)*', component: NotFound, meta: { title: '404 — Bar Experts' } },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
        top: 80
      }
    }
    return { top: 0, left: 0 }
  },
})

// Guard de navegação: valida roles antes de entrar em rotas protegidas
// Pausadas em produção até o fim do campeonato: link direto volta para a home
const PAUSED_PATHS = ['/biblioteca', '/apostilas', '/materiais', '/coqueteis', '/cocktails', '/calculadoras', '/calculators']

router.beforeEach((to, from, next) => {
  if (PAUSED_PATHS.some((p) => to.path === p || to.path.startsWith(p + '/'))) {
    return next({ path: '/' })
  }
  const authStore = useAuthStore()
  const requiredLevel = to.meta.requiresLevel ?? -1

  if ((to.meta.requiresAuth || requiredLevel > -1) && !authStore.isAuthenticated) {
    return next({ path: '/login', query: { redirect: to.fullPath } })
  }
  if (requiredLevel > authStore.roleLevel && !authStore.isSuperAdmin) {
    return next({ path: '/' })
  }
  next()
})

const app = createApp(App)
const pinia = createPinia()
app.use(pinia)

// Restaura sessão JWT ao iniciar
const authStore = useAuthStore()
authStore.restoreSession()

// Título, descrição e prévia de compartilhamento por página (src/seo/pages.js)
router.afterEach((to) => applyPageMeta(to))

// Telemetria automática de visualização de páginas
router.afterEach((to) => {
  const isAdmin = authStore.isAuthenticated && (
    authStore.roleLevel >= 4 ||
    (authStore.userEmail && authStore.userEmail.includes('depinho')) ||
    (authStore.nickname && authStore.nickname.toLowerCase().includes('depinho'))
  )
  trackPageView(to.path, { isAdmin })
})

app.use(router)
app.directive('stack-table', stackTable)

// Iniciar o efeito global de elástico (Rubberband)
initGlobalRubberband()


// -------------------------------------------------------------
// Override Global de Mouse (Restrição e Remapeamento apenas em Produção Estrita)
// -------------------------------------------------------------
if (import.meta.env.PROD && window.location.hostname !== 'dev.barexperts.com.br') {
  // 1. Desabilita o menu de contexto nativo completamente
  window.addEventListener('contextmenu', e => e.preventDefault())

  // 2. Remapeia o botão direito (button === 2) para disparar evento de click (esquerdo)
  window.addEventListener('mouseup', e => {
    if (e.button === 2) {
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
        button: 0,
        buttons: 1,
        clientX: e.clientX,
        clientY: e.clientY
      });
      e.target.dispatchEvent(clickEvent);
    }
  });
}

router.isReady().then(() => {
  app.mount('#app')
})
